import { useState, useEffect, useMemo } from "react";
import GenericModal from "../../../components/common/GenericModal";
import useServiceAreaStore from "../../../store/builders/useServiceAreaStore";
import { Plus, X, Loader2, MapPin } from "lucide-react";
import { toast } from "react-hot-toast";

const ServiceAreaExpansionModal = ({ isOpen, onClose }) => {
  const {
    builderServiceAreas,
    requestServiceAreaExpansion,
    isSubmitting,
    allServiceAreas,
    fetchAllServiceAreas,
    pendingExpansionRequests,
    fetchPendingExpansionRequests,
    isLoading: isAreaListLoading,
  } = useServiceAreaStore();

  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedTaluk, setSelectedTaluk] = useState("");
  const [expansionList, setExpansionList] = useState([]);

  /* ===========================
       LOAD SERVICE AREA OPTIONS
  ============================ */
  useEffect(() => {
    if (isOpen) {
      fetchAllServiceAreas();
      fetchPendingExpansionRequests();
    }
  }, [isOpen, fetchAllServiceAreas, fetchPendingExpansionRequests]);

  /* ===========================
       DISTRICTS LIST (SAFE)
  ============================ */
  const districts = useMemo(() => {
    if (!Array.isArray(allServiceAreas)) return [];
    const districtSet = new Set(allServiceAreas.map((area) => area.district));
    return Array.from(districtSet).sort();
  }, [allServiceAreas]);

  /* ===========================
       TALUKS FOR SELECTED DISTRICT
  ============================ */
  const taluksForSelectedDistrict = useMemo(() => {
    if (!selectedDistrict || !Array.isArray(allServiceAreas)) return [];
    return allServiceAreas.filter((area) => area.district === selectedDistrict);
  }, [selectedDistrict, allServiceAreas]);

  /* ===========================
       AVAILABLE TALUKS (SAFE)
  ============================ */
  const availableTaluks = useMemo(() => {
    if (!Array.isArray(taluksForSelectedDistrict)) return [];

    // Create set of active service areas
    const currentAreaSet = new Set(
      (builderServiceAreas || []).map((area) => area.taluk.toLowerCase())
    );

    // Create set of pending/approved expansion requests
    const pendingAreaSet = new Set(
      (pendingExpansionRequests || []).map((req) => req.taluk.toLowerCase())
    );

    // Create set of items in current expansion list
    const expansionListSet = new Set(
      (expansionList || []).map((item) => item.taluk.toLowerCase())
    );

    return taluksForSelectedDistrict.filter((area) => {
      const talukLower = area.taluk.toLowerCase();
      return (
        !currentAreaSet.has(talukLower) &&
        !pendingAreaSet.has(talukLower) &&
        !expansionListSet.has(talukLower)
      );
    });
  }, [
    taluksForSelectedDistrict,
    builderServiceAreas,
    pendingExpansionRequests,
    expansionList,
  ]);

  /* ===========================
       DISTRICT CHANGE
  ============================ */
  const handleDistrictChange = (e) => {
    setSelectedDistrict(e.target.value);
    setSelectedTaluk("");
  };

  /* ===========================
       ADD TALUK (SAFE)
  ============================ */
  const handleAddTaluk = () => {
    if (!selectedDistrict || !selectedTaluk) {
      toast.error("Please select both a district and a taluk.");
      return;
    }

    if (!Array.isArray(allServiceAreas)) {
      toast.error("Service areas not loaded yet.");
      return;
    }

    const talukObj = allServiceAreas.find(
      (t) => t.id === parseInt(selectedTaluk)
    );

    if (!talukObj) {
      toast.error("Selected taluk not found.");
      return;
    }

    setExpansionList((prev) => [
      ...prev,
      { id: talukObj.id, district: selectedDistrict, taluk: talukObj.taluk },
    ]);

    setSelectedTaluk("");
  };

  /* ===========================
        REMOVE TALUK
  ============================ */
  const handleRemoveTaluk = (talukId) => {
    setExpansionList((prev) => prev.filter((item) => item.id !== talukId));
  };

  /* ===========================
       SUBMIT REQUEST (SAFE)
  ============================ */
  const handleSubmit = async () => {
    if (expansionList.length === 0) {
      toast.error("Please add at least one taluk to your expansion request.");
      return;
    }

    const talukIds = expansionList.map((item) => item.id);

    const result = await requestServiceAreaExpansion(talukIds);
    if (result.success) {
      setExpansionList([]);
      setSelectedDistrict("");
      setSelectedTaluk("");
      onClose();
      toast.success(
        "Your request has been sent to the admin. You will be notified upon approval."
      );
    }
  };

  /* ===========================
       CLOSE MODAL
  ============================ */
  const handleModalClose = () => {
    setExpansionList([]);
    setSelectedDistrict("");
    setSelectedTaluk("");
    onClose();
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleModalClose}
      title="Request Service Area Expansion"
      size="max-w-2xl"
    >
      <div className="space-y-4">

        {/* INFO BOX */}
        <div className="bg-blue-50 border border-blue-200 text-blue-800 p-3 rounded-md text-sm">
          <strong>Note:</strong> Adding new areas requires admin approval. Once approved, 
          you will see a payment request in your dashboard to activate the new areas.
        </div>

        {/* DISTRICT + TALUK */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              District
            </label>
            <select
              value={selectedDistrict}
              onChange={handleDistrictChange}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              disabled={isAreaListLoading}
            >
              <option value="">
                {isAreaListLoading ? "Loading..." : "-- Select District --"}
              </option>

              {districts.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          {/* TALUK */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Taluk
            </label>

            <div className="flex items-center gap-2">
              <select
                value={selectedTaluk}
                onChange={(e) => setSelectedTaluk(e.target.value)}
                disabled={!selectedDistrict || availableTaluks.length === 0}
                className="w-full p-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-100"
              >
                <option value="">
                  {!selectedDistrict ? "Select District First" : "Select Taluk"}
                </option>

                {availableTaluks.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.taluk}
                  </option>
                ))}
              </select>

              <button
                onClick={handleAddTaluk}
                disabled={!selectedTaluk}
                className="p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 flex-shrink-0"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* ADDED LIST */}
        {expansionList.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-2">
              Areas to Request:
            </h4>

            <div className="space-y-2 max-h-48 overflow-y-auto border p-3 rounded-md bg-gray-50">
              {expansionList.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between items-center bg-white p-2 rounded shadow-sm"
                >
                  <div className="text-sm">
                    <span className="font-medium text-gray-700">
                      {item.taluk}
                    </span>
                    <span className="text-gray-500">, {item.district}</span>
                  </div>

                  <button
                    onClick={() => handleRemoveTaluk(item.id)}
                    className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ACTION BUTTONS */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={handleModalClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={isSubmitting || expansionList.length === 0}
            className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm flex items-center disabled:bg-gray-400"
          >
            {isSubmitting ? (
              <Loader2 className="animate-spin mr-2" />
            ) : (
              <MapPin size={16} className="mr-2" />
            )}
            Submit Request ({expansionList.length})
          </button>
        </div>

      </div>
    </GenericModal>
  );
};

export default ServiceAreaExpansionModal;
